import { useEffect, useState, type FormEvent } from 'react';
import { Sliders, RefreshCw, Plus } from 'lucide-react';
import type { FeatureFlagKey } from '@prakash-software/shared';
import { listAccounts, type AccountRow } from '../../lib/accounts-api';
import {
  clearFlagOverride,
  listFlagsAdmin,
  resetFlag,
  setFlagOverride,
  updateFlag,
  type FlagAdminRow,
} from '../../lib/feature-flags-api';
import { refreshFeatureFlags } from '../../stores/feature-flags';
import { Button, Badge } from '../../components/ui';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

// Feature flags (super-admin only). The flags themselves are declared in code
// (@prakash-software/shared FEATURE_FLAGS); here the platform operator turns
// them on or off, rolls one out to a share of accounts, and pins individual
// accounts on or off regardless. Servers pick up a change within ~15 s; open
// agent tabs on their next reconnect.

interface Draft {
  enabled: boolean;
  rolloutPercent: string;
}

function draftOf(f: FlagAdminRow): Draft {
  return {
    enabled: f.stored?.enabled ?? f.defaultEnabled,
    rolloutPercent: String(f.stored?.rolloutPercent ?? 100),
  };
}

function summary(f: FlagAdminRow): string {
  if (!f.stored) return `default · ${f.defaultEnabled ? 'on' : 'off'}`;
  if (!f.stored.enabled) return 'off';
  return f.stored.rolloutPercent >= 100 ? 'on' : `on · ${f.stored.rolloutPercent}% of accounts`;
}

type Run = (key: FeatureFlagKey, fn: () => Promise<unknown>, failure: string) => Promise<boolean>;

export function AdminFeatureFlags() {
  const [flags, setFlags] = useState<FlagAdminRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [drafts, setDrafts] = useState<Partial<Record<FeatureFlagKey, Draft>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<FeatureFlagKey | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setError(null);
    try {
      const [f, a] = await Promise.all([listFlagsAdmin(), listAccounts()]);
      setFlags(f);
      setAccounts(a);
      setDrafts(Object.fromEntries(f.map((x) => [x.key, draftOf(x)])));
    } catch {
      setError('Could not load feature flags.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  /** Run a write, then reload this page and the operator's own flags. */
  const run: Run = async (key, fn, failure) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
      void refreshFeatureFlags();
      return true;
    } catch {
      setError(failure);
      return false;
    } finally {
      setBusy(null);
    }
  };

  function save(e: FormEvent, f: FlagAdminRow) {
    e.preventDefault();
    const d = drafts[f.key] ?? draftOf(f);
    const rolloutPercent = Math.min(100, Math.max(0, Math.round(Number(d.rolloutPercent) || 0)));
    void run(f.key, () => updateFlag(f.key, { enabled: d.enabled, rolloutPercent }), `Could not save ${f.key}.`);
  }

  async function reset(f: FlagAdminRow) {
    if (!(await confirmDialog({ title: `Reset ${f.key} to its default?`, message: `${f.defaultEnabled ? 'On' : 'Off'} for every account. Account overrides stay.`, tone: 'default', confirmLabel: 'Reset' }))) return;
    void run(f.key, () => resetFlag(f.key), `Could not reset ${f.key}.`);
  }

  const setDraft = (f: FlagAdminRow, patch: Partial<Draft>) =>
    setDrafts((ds) => ({ ...ds, [f.key]: { ...(ds[f.key] ?? draftOf(f)), ...patch } }));

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Sliders size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>Feature Flags</h1>
          <p className={styles.subtitle}>
            Roll out capabilities incrementally, pin tenant account overrides, and toggle platform switches safely.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => void load()}
          className="gap-2 h-9 px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </Button>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}
      {loading && <p style={{ color: 'var(--inic-ink-muted)', fontSize: 13 }}>Loading feature flags…</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {flags.map((f) => {
          const d = drafts[f.key] ?? draftOf(f);
          return (
            <div
              key={f.key}
              style={{
                background: 'var(--inic-card-bg)',
                border: '1px solid var(--inic-card-border)',
                borderRadius: 12,
                padding: 24,
                boxShadow: 'var(--inic-shadow-sm)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span className={styles.mono} style={{ fontSize: 15, fontWeight: 700, color: 'var(--inic-ink)' }}>
                  {f.key}
                </span>
                <Badge
                  variant="outline"
                  className={`text-xs px-2.5 py-0.5 font-mono ${
                    f.stored?.enabled
                      ? 'text-emerald-500 border-emerald-500/20 bg-emerald-500/10'
                      : 'text-muted-foreground'
                  }`}
                >
                  {summary(f)}
                </Badge>
              </div>
              <p style={{ color: 'var(--inic-ink-muted)', fontSize: 13, margin: '0 0 16px', lineHeight: 1.5 }}>
                {f.description}
              </p>

              <form onSubmit={(e) => save(e, f)} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
                <label className={styles.field} style={{ minWidth: 120 }}>
                  <span className={styles.label}>State</span>
                  <select
                    className={styles.select}
                    value={d.enabled ? '1' : '0'}
                    onChange={(e) => setDraft(f, { enabled: e.target.value === '1' })}
                    style={{ minHeight: 38, height: 38, padding: '4px 10px' }}
                  >
                    <option value="1">On</option>
                    <option value="0">Off</option>
                  </select>
                </label>
                <label className={styles.field} style={{ minWidth: 160 }}>
                  <span className={styles.label}>Rollout (% of accounts)</span>
                  <input
                    className={styles.input}
                    type="number"
                    min={0}
                    max={100}
                    required
                    disabled={!d.enabled}
                    value={d.rolloutPercent}
                    onChange={(e) => setDraft(f, { rolloutPercent: e.target.value })}
                    style={{ minHeight: 38, height: 38, padding: '4px 10px' }}
                  />
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button type="submit" size="sm" disabled={busy === f.key} className="h-9 px-4 text-xs font-semibold">
                    {busy === f.key ? 'Saving…' : 'Save'}
                  </Button>
                  {f.stored && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy === f.key}
                      onClick={() => reset(f)}
                      className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Reset to Default
                    </Button>
                  )}
                </div>
              </form>

              <OverridesTable flag={f} accounts={accounts} busy={busy === f.key} run={run} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverridesTable({
  flag,
  accounts,
  busy,
  run,
}: {
  flag: FlagAdminRow;
  accounts: AccountRow[];
  busy: boolean;
  run: Run;
}) {
  const [accountId, setAccountId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const overridden = new Set(flag.overrides.map((o) => o.accountId));
  const available = accounts.filter((a) => !overridden.has(a.id));

  function add(e: FormEvent) {
    e.preventDefault();
    if (!accountId) return;
    void run(flag.key, () => setFlagOverride(flag.key, accountId, enabled), 'Could not add the override.').then(
      (ok) => ok && setAccountId(''),
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--inic-rule)', paddingTop: 16 }}>
      <div className={styles.tableWrap} style={{ margin: 0 }}>
        <table className={styles.table}>
          <thead>
            <tr><th>Account Override</th><th>Pinned State</th><th>Updated</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
          </thead>
          <tbody>
            {flag.overrides.map((o) => (
              <tr key={o.accountId}>
                <td>
                  <span style={{ fontWeight: 600 }}>{o.accountName}</span>{' '}
                  <span className={styles.mono} style={{ color: 'var(--inic-ink-subtle)', fontSize: 11 }}>
                    ({o.accountSlug})
                  </span>
                </td>
                <td>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-mono ${
                      o.enabled
                        ? 'text-emerald-500 border-emerald-500/20 bg-emerald-500/10'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {o.enabled ? 'forced on' : 'forced off'}
                  </Badge>
                </td>
                <td style={{ fontSize: 12, color: 'var(--inic-ink-muted)' }}>{new Date(o.updatedAt).toLocaleDateString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs px-2 text-destructive hover:bg-destructive/10"
                    disabled={busy}
                    onClick={() => void run(flag.key, () => clearFlagOverride(flag.key, o.accountId), 'Could not remove the override.')}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {flag.overrides.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--inic-ink-subtle)', padding: 12, fontSize: 12 }}>No account overrides configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {available.length > 0 && (
        <form onSubmit={add} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 14 }}>
          <label className={styles.field} style={{ minWidth: 200, flex: 1 }}>
            <span className={styles.label}>Account Override</span>
            <select
              className={styles.select}
              required
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              style={{ minHeight: 36, height: 36, padding: '4px 10px', fontSize: 13 }}
            >
              <option value="">Choose an account…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.slug})</option>
              ))}
            </select>
          </label>
          <label className={styles.field} style={{ minWidth: 100 }}>
            <span className={styles.label}>Force</span>
            <select
              className={styles.select}
              value={enabled ? '1' : '0'}
              onChange={(e) => setEnabled(e.target.value === '1')}
              style={{ minHeight: 36, height: 36, padding: '4px 10px', fontSize: 13 }}
            >
              <option value="1">On</option>
              <option value="0">Off</option>
            </select>
          </label>
          <Button
            type="submit"
            variant="outline"
            size="sm"
            disabled={busy || !accountId}
            className="h-9 px-3 gap-1.5 text-xs font-semibold"
          >
            <Plus size={13} />
            <span>Add Override</span>
          </Button>
        </form>
      )}
    </div>
  );
}
